import { type FormEvent, type MouseEvent } from 'react';
import styled from '@emotion/styled';
import { mediaQuery } from '@styles/index';
import useMobileLoginCountries from '@api/hooks/useMobileLoginCountries';
import CountryMenu from '@cont/Login/CountryMenu';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useLocalLoginInfo } from '@cont/Login/Context/LoginLocalInfo';
import Constants, {
  CONTINUE_WITH_EMAIL,
  CONTINUE_WITH_PHONE,
  defaultCountry
} from '@cont/Login/constants';

const CountryMenuSlot = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    width: 24px;
    height: auto;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 464px;
  max-width: 100%;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    gap: 16px;
    padding: 16px;
    border-radius: 12px;
    background: rgba(9, 7, 19, 0.32);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  & .campaign-auth-country-menu.MuiPopover-root {
    z-index: 30 !important;
  }

  & .campaign-auth-country-menu .MuiPaper-root {
    background: #120d23 !important;
    background-color: #120d23 !important;
    border: 1px solid rgba(255, 255, 255, 0.25) !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55) !important;
    opacity: 1 !important;
  }

  & .campaign-auth-country-menu .MuiMenu-list li {
    color: #ffffff;
  }

  & .campaign-auth-country-menu .MuiMenu-list li:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  & .campaign-auth-country-menu .MuiMenu-list span {
    color: #ffffff !important;
  }

  & ${CountryMenuSlot} p,
  & ${CountryMenuSlot} svg {
    color: #ffffff !important;
  }
`;

const PhoneFieldSection = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  margin-top: 32px;

  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const Title = styled.h1`
  margin: 0;
  color: #fff;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;

  ${mediaQuery.uptoMobile} {
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 12px;

  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const OrText = styled.span`
  color: #a2a0a2;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
`;

const ToggleLink = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;

  ${mediaQuery.uptoMobile} {
    font-size: 12px;
  }
`;

const FieldShell = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  height: 56px;
  margin-top: 0;
  padding: 0 24px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 12px;

  ${mediaQuery.uptoMobile} {
    height: 48px;
    margin-top: 0;
    padding: 0 16px;
    border-color: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
  }
`;

const TextInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;

  &::placeholder {
    color: #bebcc3;
    font-weight: 600;
  }

  ${mediaQuery.uptoMobile} {
    font-size: 12px;
    font-weight: 400;

    &::placeholder {
      font-weight: 400;
    }
  }
`;

const ErrorText = styled.p`
  margin: 8px 0 0;
  color: #eb5757;
  font-size: 12px;
  line-height: 18px;
`;

const Actions = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;

  ${mediaQuery.uptoMobile} {
    margin-top: 0;
  }
`;

const LoginButton = styled.button`
  width: 176px;
  height: 48px;
  padding: 0 24px;
  border: 0;
  border-radius: 8px;
  background: #fff;
  color: #3c4852;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }

  ${mediaQuery.uptoMobile} {
    width: 128px;
    border-radius: 4px;
  }
`;

const AltLink = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-decoration: underline;
  text-underline-offset: 5px;
  cursor: pointer;

  ${mediaQuery.uptoMobile} {
    font-size: 12px;
  }
`;

const CampaignAuthForm = () => {
  const {
    accountType,
    changeAccountType,
    changeLoginType,
    country,
    changeCountry,
    mobileNumber,
    setMobileNumber,
    email,
    setEmail,
    loginType,
    numberStepErrorMsg: errorMsg,
    submitNumber,
    submitEmail,
    logSignUpSteps
  } = useLocalLoginInfo();
  const smsEligibleCountries = useMobileLoginCountries();
  const menuCountries =
    smsEligibleCountries.length > 0 ? smsEligibleCountries : [defaultCountry];
  const goalSegmentData = useGoalEventProps({});

  const isEmail = loginType === Constants.LOGIN_TYPE_EMAIL;
  const isLoginAccountType = accountType === Constants.ACCOUNT_TYPE_LOGIN;
  const isIndia = country?.code === 'IN';
  const validMobile =
    Boolean(mobileNumber?.match(/^\d+$/)) &&
    (!isIndia || mobileNumber.length === 10);
  const canSubmit = isEmail ? Boolean(email) : validMobile;
  const phoneMaxLength = isIndia ? 10 : 15;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    if (isEmail) submitEmail();
    else submitNumber();
  };

  const toggleAccountType = (event: MouseEvent) => {
    event.preventDefault();
    changeAccountType();
  };

  const switchLoginType = (event: MouseEvent) => {
    event.preventDefault();
    segment.track(isEmail ? CONTINUE_WITH_PHONE : CONTINUE_WITH_EMAIL, {
      ...goalSegmentData,
      curtain_type: 'side-curtain'
    });
    changeLoginType();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Title>{isLoginAccountType ? 'Login' : 'Join Unacademy'}</Title>
      <ToggleRow>
        <OrText>or</OrText>
        <ToggleLink type="button" onClick={toggleAccountType}>
          {isLoginAccountType ? 'create your account' : 'login to your account'}
        </ToggleLink>
      </ToggleRow>
      <PhoneFieldSection>
        <FieldShell>
          {!isEmail && country ? (
            <CountryMenuSlot>
              <CountryMenu
                changeCountry={changeCountry}
                country={country}
                countries={menuCountries}
                menuClassName="campaign-auth-country-menu"
              />
            </CountryMenuSlot>
          ) : null}
          <TextInput
            type={isEmail ? 'email' : 'tel'}
            inputMode={isEmail ? 'email' : 'numeric'}
            autoFocus
            maxLength={isEmail ? undefined : phoneMaxLength}
            value={isEmail ? email : mobileNumber}
            placeholder={isEmail ? 'Email address' : 'Enter mobile number'}
            onChange={(event) => {
              if (isEmail) {
                setEmail(event.target.value.replace(/ /g, ''));
                return;
              }
              const phone = event.target.value.replace(/ /g, '');
              if (phone.match(/^\d+$/) || phone === '') {
                if (!(isIndia && phone.length > 10)) setMobileNumber(phone);
              }
            }}
            onFocus={logSignUpSteps?.(
              isEmail
                ? Constants.SEGMENT_SIGNUP_FIELD_NAMES.EMAIL
                : Constants.SEGMENT_SIGNUP_FIELD_NAMES.PHONE_NUMBER
            )}
          />
        </FieldShell>
      </PhoneFieldSection>
      {errorMsg ? <ErrorText>{errorMsg}</ErrorText> : null}
      <Actions>
        <LoginButton type="submit" disabled={!canSubmit}>
          {isLoginAccountType ? 'Login' : 'Continue'}
        </LoginButton>
        <AltLink type="button" onClick={switchLoginType}>
          {isEmail ? 'Continue with mobile number' : 'Continue with email'}
        </AltLink>
      </Actions>
    </Form>
  );
};

export default CampaignAuthForm;
